import React from 'react';
import PropTypes from 'prop-types';
import useExperiment from './useExperiment';
import useForcedExperiment from './useForcedExperiment';
import ExperimentContext from './ExperimentContext';
import Logger from '../../utils/logger';

const VariationSelector = ({
  children, flagKey, checkVariationCount, fallbackKey, variationKeyList,
}) => {
  const [isEnabled, variationKey] = useExperiment(flagKey);
  const [isForcedEnabled, forcedVariationKey] = useForcedExperiment(flagKey);

  // 1. Confirm Variation counts are conformal
  const variationCount = React.Children.count(children);
  if (!checkVariationCount(variationCount)) {
    Logger.error(`The "${flagKey}" experiment fails required Variation count check`);
    return null;
  }

  // 2. Confirm when withFallback, fallback in key set
  const variationKeySet = new Set(variationKeyList);
  const isWithFallback = !!(fallbackKey && variationKeySet.size);

  if (isWithFallback && !variationKeySet.has(fallbackKey)) {
    Logger.error(`In the "${flagKey}" experiment, fallbackKey "${fallbackKey}" is not a member of variationKeyList`);
    return null;
  }

  // 3. Confirm when withFallback, key set matches Variation count
  if (isWithFallback && variationKeySet.size !== variationCount) {
    Logger.error(`In the "${flagKey}" experiment, the variationKeyList unique count does not match the Variation count`);
    return null;
  }

  // 4. Get values from API / Redux state
  let selectedVariation = isEnabled ? variationKey : undefined;

  // 5. Check for forced values and allow forced values to override
  selectedVariation = isForcedEnabled ? forcedVariationKey : selectedVariation;

  // 6. Check for a valid selectedVariation when using fallback
  const isValidVariation = isWithFallback ? variationKeySet.has(selectedVariation) : true;

  // 7. Confirm experiment is enabled and can render a valid Variation.
  //    If not, use fallback if defined, otherwise return null render.
  if (!(isEnabled || isForcedEnabled) || !selectedVariation || !isValidVariation) {
    if (isWithFallback) {
      selectedVariation = fallbackKey;
    } else {
      return null;
    }
  }

  // ExperimentContext, value can grow as future needs dictate
  const contextValue = { flagKey, selectedVariation };

  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
    </ExperimentContext.Provider>
  );
};

VariationSelector.propTypes = {
  checkVariationCount: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  fallbackKey: PropTypes.string,
  flagKey: PropTypes.string.isRequired,
  variationKeyList: PropTypes.arrayOf(PropTypes.string),
};

VariationSelector.defaultProps = {
  fallbackKey: '',
  variationKeyList: [],
};

export default VariationSelector;
